import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import * as scheduleService from '../../services/scheduleService';
import * as authService from '../../services/authService';
import { Modal } from '../Common/Modal';
import { useToast } from '../Common/Toast';
import { DURATIONS } from '../../utils/constants';

const DEFAULT_COLORS = [
  '#607D8B', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
  '#2196F3', '#00BCD4', '#009688', '#4CAF50', '#8BC34A',
  '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800', '#FF5722',
];

export function ShowManager() {
  const [shows, setShows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingShow, setEditingShow] = useState(null);
  const { hasRole } = useAuth();
  const { addToast } = useToast();
  const isAdmin = hasRole('admin');

  const fetchShows = async () => {
    setLoading(true);
    try {
      const data = await scheduleService.getShows();
      setShows(data);
    } catch (err) {
      addToast('Erreur lors du chargement des émissions', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShows();
    if (isAdmin) {
      authService.getUsers().then(setUsers).catch(() => {});
    }
  }, [isAdmin]);

  const handleAdd = () => {
    setEditingShow(null);
    setModalOpen(true);
  };

  const handleEdit = (show) => {
    setEditingShow(show);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette émission ?')) return;
    try {
      await scheduleService.deleteShow(id);
      addToast('Émission supprimée', 'success');
      fetchShows();
    } catch (err) {
      addToast('Erreur lors de la suppression', 'error');
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingShow) {
        await scheduleService.updateShow(editingShow.id, data);
        addToast('Émission mise à jour', 'success');
      } else {
        await scheduleService.createShow(data);
        addToast('Émission créée', 'success');
      }
      setModalOpen(false);
      fetchShows();
    } catch (err) {
      addToast(err.response?.data?.message || 'Erreur lors de l\'enregistrement', 'error');
      throw err;
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Émissions</h1>
        {isAdmin && (
          <button type="button" className="btn btn-primary" onClick={handleAdd}>
            + Nouvelle émission
          </button>
        )}
      </div>

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Couleur</th>
                <th>Initiales</th>
                <th>Nom</th>
                <th>Catégorie</th>
                <th>Durée</th>
                {isAdmin && <th>Éditeur</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shows.map((show) => (
                <tr key={show.id}>
                  <td>
                    <span
                      className="color-swatch"
                      style={{ backgroundColor: show.color || '#607D8B' }}
                    />
                  </td>
                  <td>
                    <span className="show-initials-badge-sm">{show.initials || '—'}</span>
                  </td>
                  <td>{show.name}</td>
                  <td>{show.category_name || '—'}</td>
                  <td>{show.default_duration || 30} min</td>
                  {isAdmin && <td>{show.editor_name || '—'}</td>}
                  <td className="actions-cell">
                    <Link
                      to={`/shows/${show.id}/episodes`}
                      className="btn btn-secondary btn-sm"
                    >
                      Épisodes
                    </Link>
                    {isAdmin && (
                      <>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleEdit(show)}
                        >
                          Modifier
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(show.id)}
                        >
                          Supprimer
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && (
        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={editingShow ? 'Modifier l\'émission' : 'Nouvelle émission'}
          size="md"
        >
          <ShowForm
            show={editingShow}
            users={users}
            onSave={handleSave}
            onCancel={() => setModalOpen(false)}
          />
        </Modal>
      )}
    </div>
  );
}

function ShowForm({ show, users, onSave, onCancel }) {
  const [name, setName] = useState(show?.name ?? '');
  const [description, setDescription] = useState(show?.description ?? '');
  const [category, setCategory] = useState(show?.category_id ?? '');
  const [defaultDuration, setDefaultDuration] = useState(show?.default_duration ?? 30);
  const [color, setColor] = useState(show?.color ?? DEFAULT_COLORS[0]);
  const [initials, setInitials] = useState(show?.initials ?? '');
  const [userId, setUserId] = useState(show?.user_id ?? '');
  const [active, setActive] = useState(show?.is_active !== 0);
  const [saving, setSaving] = useState(false);

  const editors = (users || []).filter(
    (u) => u.role === 'editor' || u.role === 'admin'
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        name,
        description,
        category_id: category || null,
        default_duration: defaultDuration,
        color,
        initials: initials || null,
        user_id: userId ? parseInt(userId, 10) : null,
        is_active: active ? 1 : 0,
      });
    } catch {
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-row">
        <div className="form-group" style={{ flex: 2 }}>
          <label>Nom *</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Initiales</label>
          <input type="text" value={initials} onChange={(e) => setInitials(e.target.value.toUpperCase())} maxLength={10} placeholder="ex: DDF" />
        </div>
      </div>
      <div className="form-group">
        <label>Description de la série</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Texte descriptif de la série pour les cue sheets et le XML..." />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Durée par défaut</label>
          <select value={defaultDuration} onChange={(e) => setDefaultDuration(parseInt(e.target.value, 10))}>
            {DURATIONS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Éditeur / Responsable</label>
          <select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">— Aucun —</option>
            {editors.map((u) => (
              <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label>Couleur</label>
        <div className="color-picker-row">
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="color-input" />
          <input type="text" value={color} onChange={(e) => setColor(e.target.value)} className="color-text" />
        </div>
      </div>
      <div className="form-group">
        <label className="checkbox-label">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Actif
        </label>
      </div>
      <div className="modal-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Annuler</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
}
